/**
 * `CalibrationProfile` (architecture §20) and the bridge between it and the detectors.
 *
 * The profile is architecture §20's shape, kept literally: a flat `Record<string, number>` of
 * baseline measurements, a quality figure, the capabilities this sitting actually measured, and
 * nothing that could describe a face, a name or a body beyond the handful of scalars GDS §13 lists.
 * It is created per player, per sitting, and stored on the camera session — never in a repository,
 * never under a `thrive.v1.*` key (GDS §13 "store session-only").
 *
 * The detectors read a nested {@link PoseCalibrationThresholds}, so exactly two functions cross
 * between the two shapes. That is the whole of CAM-02's "adjusts detector thresholds": if a field
 * stops crossing, a measured child silently reverts to Slice B's flagged placeholder average, and
 * `calibration-thresholds.test.ts` is what notices.
 *
 * **One field is not in architecture §20:** `cameraMode`. §20's `presentationMode` is the
 * AR_CAMERA/RENDERED_SCENE/HYBRID axis of §5, which is a property of the *game*, not of a sitting;
 * Compact vs Full-Motion (GDS §6) is a different axis entirely and it changes what step 6 measures.
 * Adding a field beside §20's rather than overloading `presentationMode` keeps both honest. Flagged
 * for the orchestrator.
 */

import type { InputCapability } from '../../shared/types/capabilities';
import type { PresentationMode, Viewport } from '../../shared/types/presentation';
import type { CameraMode } from '../camera/readiness';
import { DEFAULT_POSE_CALIBRATION, type PoseCalibrationThresholds } from '../pose/pose-calibration';

/** Architecture §20, plus the Compact/Full-Motion axis (see the file comment). */
export interface CalibrationProfile {
  readonly playerId: string;
  readonly cameraDeviceId?: string | undefined;
  readonly viewport: Viewport;
  readonly presentationMode: PresentationMode;
  /** GDS §6's play-space mode this calibration was measured in. */
  readonly cameraMode: CameraMode;
  readonly supportedCapabilities: readonly InputCapability[];
  readonly baselineMeasurements: Readonly<Record<string, number>>;
  /** 0–1. How much of the body was measured, and how well it could be seen while measuring. */
  readonly quality: number;
  readonly createdAt: string;
}

/**
 * Every measurement name a profile may carry.
 *
 * Covers GDS §13's session-only list: standing baseline, body scale, torso midpoint, lane bounds,
 * hands-raised / crouch / jump thresholds. Confidence is the profile's `quality`; the player ring
 * is a visual and lives on the screen, not in a number.
 */
export const CALIBRATION_MEASUREMENT_KEYS = [
  'standingHipY',
  'bodyScale',
  'torsoMidpointX',
  'torsoMidpointY',
  'laneLeftMax',
  'laneRightMin',
  'laneHysteresis',
  'jumpRiseScale',
  'crouchDropScale',
  'handsRaiseScale',
  'handsRaisedWristCount',
  'stillnessSpeedScale',
  'hysteresisFraction',
  /** Compact only: how far a child reaches when they are asked for a big reach (GDS §15). */
  'substituteReachScale',
] as const;

export type CalibrationMeasurementKey = (typeof CALIBRATION_MEASUREMENT_KEYS)[number];

/** Flattens the detector thresholds into architecture §20's `Record<string, number>`. */
export function baselineMeasurementsFrom(
  thresholds: PoseCalibrationThresholds,
  extra: Partial<Record<CalibrationMeasurementKey, number>> = {},
): Record<string, number> {
  const measurements: Record<string, number> = {
    standingHipY: thresholds.standingHipY,
    bodyScale: thresholds.bodyScale,
    laneLeftMax: thresholds.laneBounds.leftMax,
    laneRightMin: thresholds.laneBounds.rightMin,
    laneHysteresis: thresholds.laneHysteresis,
    jumpRiseScale: thresholds.jumpRiseScale,
    crouchDropScale: thresholds.crouchDropScale,
    handsRaiseScale: thresholds.handsRaiseScale,
    substituteReachScale: thresholds.substituteReachScale,
    handsRaisedWristCount: thresholds.handsRaisedWristCount,
    stillnessSpeedScale: thresholds.stillnessSpeedScale,
    hysteresisFraction: thresholds.hysteresisFraction,
  };

  for (const [key, value] of Object.entries(extra)) {
    if (typeof value === 'number' && Number.isFinite(value)) measurements[key] = value;
  }

  return measurements;
}

function reading(
  measurements: Readonly<Record<string, number>>,
  key: CalibrationMeasurementKey,
  fallback: number,
): number {
  const value = measurements[key];
  // A measurement that is missing or not a number falls back to the flagged Slice B placeholder,
  // never to zero: a threshold of zero would make every twitch an answer.
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

/**
 * Expands the measurements back into what the detectors read.
 *
 * Missing fields fall back to {@link DEFAULT_POSE_CALIBRATION} — which is what a skipped step
 * leaves behind. That is safe *because* the skipped step's capability is excluded from the
 * profile: a consumer that asked for JUMP is told the sitting cannot do jumps and substitutes,
 * rather than reading a placeholder threshold and believing it.
 */
function isProfile(
  source: Readonly<Record<string, number>> | CalibrationProfile,
): source is CalibrationProfile {
  return typeof (source as CalibrationProfile).baselineMeasurements === 'object';
}

export function poseCalibrationFrom(
  source: Readonly<Record<string, number>> | CalibrationProfile,
): PoseCalibrationThresholds {
  // A profile or the bare measurements: both are accepted because both call sites exist, and a
  // `Record<string, number>` may legitimately have a key called `baselineMeasurements`, so the
  // narrowing is stated rather than inferred.
  const measurements: Readonly<Record<string, number>> = isProfile(source)
    ? source.baselineMeasurements
    : source;

  return {
    standingHipY: reading(measurements, 'standingHipY', DEFAULT_POSE_CALIBRATION.standingHipY),
    bodyScale: reading(measurements, 'bodyScale', DEFAULT_POSE_CALIBRATION.bodyScale),
    laneBounds: {
      leftMax: reading(measurements, 'laneLeftMax', DEFAULT_POSE_CALIBRATION.laneBounds.leftMax),
      rightMin: reading(measurements, 'laneRightMin', DEFAULT_POSE_CALIBRATION.laneBounds.rightMin),
    },
    laneHysteresis: reading(
      measurements,
      'laneHysteresis',
      DEFAULT_POSE_CALIBRATION.laneHysteresis,
    ),
    jumpRiseScale: reading(measurements, 'jumpRiseScale', DEFAULT_POSE_CALIBRATION.jumpRiseScale),
    crouchDropScale: reading(
      measurements,
      'crouchDropScale',
      DEFAULT_POSE_CALIBRATION.crouchDropScale,
    ),
    handsRaiseScale: reading(
      measurements,
      'handsRaiseScale',
      DEFAULT_POSE_CALIBRATION.handsRaiseScale,
    ),
    // Only a Compact sitting's step 6 measures this; a Full-Motion profile falls back to the
    // placeholder, which is harmless because a Full-Motion sitting counts with jumps.
    substituteReachScale: reading(
      measurements,
      'substituteReachScale',
      DEFAULT_POSE_CALIBRATION.substituteReachScale,
    ),
    handsRaisedWristCount: reading(
      measurements,
      'handsRaisedWristCount',
      DEFAULT_POSE_CALIBRATION.handsRaisedWristCount,
    ),
    stillnessSpeedScale: reading(
      measurements,
      'stillnessSpeedScale',
      DEFAULT_POSE_CALIBRATION.stillnessSpeedScale,
    ),
    hysteresisFraction: reading(
      measurements,
      'hysteresisFraction',
      DEFAULT_POSE_CALIBRATION.hysteresisFraction,
    ),
  };
}

export interface QualityInput {
  /** One confidence per captured measurement step. */
  readonly confidences: readonly number[];
  /** How many steps this sitting *could* have measured. */
  readonly measurableSteps: number;
}

/**
 * Quality: how well the child could be seen, times how much of them was measured.
 *
 * Both halves matter and neither is enough alone. A sitting that measured two moves perfectly is
 * not a good calibration of a child who needs five; and five moves measured through a dark room
 * are five numbers nobody should trust. Reporting either half as the whole would be the flattering
 * version of this number, and a flattering quality figure is exactly what makes the reuse rules
 * (architecture §20) worthless.
 */
export function deriveQuality(input: QualityInput): number {
  const { confidences, measurableSteps } = input;
  if (confidences.length === 0 || measurableSteps <= 0) return 0;

  const mean = confidences.reduce((total, value) => total + value, 0) / confidences.length;
  const coverage = Math.min(1, confidences.length / measurableSteps);
  return Math.max(0, Math.min(1, mean * coverage));
}
